import React from "react";
import { BarChart, Legend, LineChart, type MouseHandlerDataParam } from "recharts";
import { z } from "zod";

import { Metric } from "~/models/metric";
import { cx } from "~/tools/class-variance";
import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { DEFAULT_FORMATTER_UNIT, type FormatterUnit } from "~/tools/formatter-unit";
import { type Scope, batch, untrack } from "~/tools/impulse";
import { max } from "~/tools/max";
import { prop } from "~/tools/prop";
import { usePermanent } from "~/tools/use-permanent";

import { SpecificRange, type TimeRange } from "../../app/state/view/timeline/TimeRange";
import { type TimeRangeHistory, useTimeRangeHistory } from "../react/timeline/time-range-history-utils";
import { useTimestampCursor, useTimestampCursorState } from "../TimestampCursor";

import { Bar } from "./_bar";
import { createChartDataPoints } from "./_create-chart-data-points";
import { GlobalCursor } from "./_global-cursor";
import { Grid } from "./_grid";
import { LegendContent, type LegendContentMode, type LegendTableCalculatedValueColumn } from "./_legend-content";
import { Line } from "./_line";
import { ThresholdReferenceLine } from "./_threshold-reference-line";
import styles from "./_time-series-chart.module.css";
import { TimestampCursor } from "./_timestamp-cursor";
import { TimestampMarkers } from "./_timestamp-markers";
import { Tooltip } from "./_tooltip";
import { useVisibleTimeSeries } from "./_use-visible-time-series";
import { XAxis } from "./_x-axis";
import { YAxis, type YAxisProps } from "./_y-axis";
import { BrushArea } from "./brush-area";
import { TimeSeriesChartState } from "./state";
import type { TimeSeries } from "./time-series";

const DEFAULT_CHART_LEGEND_MAX_LINES = 5;
const Y_AXIS_PADDING_TOP_PX = 15;
const Y_AXIS_LABEL_WIDTH_PX = 20;

function callWithTimestamp({ activeLabel }: MouseHandlerDataParam, fn: (value: number, scope: Scope) => void) {
	const timestamp = z.coerce.number().safeParse(activeLabel);

	if (timestamp.success) {
		batch((scope) => {
			fn(timestamp.data, scope);
		});
	}
}

export type TimeSeriesChartType = "line" | "bar";

export type TimeSeriesChartYAxis = Pick<YAxisProps, "label" | "show" | "min" | "max">;

export interface TimeSeriesChartLegend {
	/**
	 * @default true
	 */
	show?: boolean;

	/**
	 * @default "bottom"
	 */
	position?: "bottom" | "right";

	/**
	 * @default "list"
	 */
	mode?: LegendContentMode;
	columns?: ReadonlyArray<LegendTableCalculatedValueColumn>;
}

export interface ThresholdLine {
	color: string;
	y: number;
	mode: "absolute" | "percent";
	label: string;
}

export interface TimeSeriesChartProps {
	timeRange: TimeRange;
	timeSeries: ReadonlyArray<TimeSeries>;
	unit?: FormatterUnit;
	onBrush?: (start: number, end: number) => void;
	yAxis?: TimeSeriesChartYAxis;
	legend?: TimeSeriesChartLegend;
	canvasHeight?: number;
	legendMaxLines?: number;
	maxVisibleTimeSeries?: {
		count: number;
		onShowAll: VoidFunction;
	};
	tooltipViewBoxElement?: null | HTMLElement;
	markerTimestamp?: Time;
	/**
	 * @default "line"
	 */
	type?: TimeSeriesChartType;
	connectNulls?: boolean;
	thresholdLines?: ReadonlyArray<ThresholdLine>;
	decimalPlaces?: number;
	state?: TimeSeriesChartState;
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: it can't shrink further
export const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
	timeRange,
	timeSeries,
	unit = DEFAULT_FORMATTER_UNIT,
	canvasHeight,
	onBrush,
	yAxis = {},
	legend: {
		show: showLegend = true,
		position: legendPosition = "bottom" as const,
		mode: legendMode = "list" as const,
		columns: legendColumns = [],
	} = {},
	legendMaxLines = DEFAULT_CHART_LEGEND_MAX_LINES,
	maxVisibleTimeSeries,
	tooltipViewBoxElement = null,
	markerTimestamp,
	type = "line",
	connectNulls = false,
	thresholdLines,
	decimalPlaces,
	state: stateFromProps,
}) => {
	const ChartElement = type === "line" ? LineChart : BarChart;

	const containerRef = React.useRef<React.ComponentRef<typeof ChartElement>>(null);
	const [legendPortalElement, setLegendPortalElement] = React.useState<HTMLDivElement | null>(null);
	const timestampCursor = useTimestampCursor();
	const timestampCursorState = useTimestampCursorState();
	const timeRangeHistory = useTimeRangeHistory();
	const fallbackState = usePermanent(() => new TimeSeriesChartState());
	const state = stateFromProps ?? fallbackState;

	const maxVisibleTimeSeriesCount = maxVisibleTimeSeries?.count;
	const isBottomLegend = legendPosition === "bottom";

	const [visibleTimeSeries, specificTimeRange] = useVisibleTimeSeries({
		timeRange,
		timeSeries,
		maxVisibleTimeSeriesCount,
	});

	const points = React.useMemo(
		() => createChartDataPoints(specificTimeRange, visibleTimeSeries),
		[visibleTimeSeries, specificTimeRange],
	);

	const thresholds = React.useMemo(() => {
		const largestDataPoint = max(0, points.flatMap(prop("values")));

		return (
			thresholdLines?.map((threshold) => ({
				value: threshold.mode === "absolute" ? threshold.y : largestDataPoint * (threshold.y / 100),
				stroke: threshold.color,
				name: threshold.label,
			})) ?? []
		);
	}, [thresholdLines, points]);

	const labels = React.useMemo(
		() => visibleTimeSeries.map(({ metric, alias }) => Metric.toLabel(alias, metric)),
		[visibleTimeSeries],
	);

	return (
		<div
			className={cx(
				"group/time-series-chart flex size-full min-h-0 min-w-0",
				isBottomLegend ? "flex-col gap-2" : "flex-row gap-4",
			)}
		>
			<ChartElement
				className={styles.chart}
				data={points}
				height={isBottomLegend ? (canvasHeight ?? "100%") : "100%"}
				onClick={(param) => {
					untrack((scope) => {
						if (timestampCursorState.isHotkeyPressed(scope)) {
							callWithTimestamp(param, (timestamp) => timestampCursorState.toggleMarker(timestamp));
						}
					});
				}}
				onMouseDown={(param) => {
					if (onBrush != null) {
						callWithTimestamp(param, (timestamp) => {
							state.brushArea.onBrushStart(timestamp);
						});
					}
				}}
				onMouseLeave={() => {
					batch(() => {
						state.brushArea.onBrushEnd();
						timestampCursor.setValue(undefined);
					});
				}}
				onMouseMove={(param) => {
					/*
						If the tooltip (solid line) ever becomes inactive (hidden)
						we should also hide the timestampCursor (dashed line)
					*/
					if (!param.isTooltipActive) {
						timestampCursor.setValue(undefined);
						return;
					}

					callWithTimestamp(param, (timestamp) => {
						if (onBrush != null) {
							state.brushArea.onBrushMove(timestamp);
						}
						timestampCursor.setValue(timestamp);
					});
				}}
				onMouseUp={(param) => {
					if (onBrush != null) {
						callWithTimestamp(param, (timestamp, scope) => {
							state.brushArea.onBrushMove(timestamp);

							const interval = state.brushArea.interval.getValue(scope);

							state.brushArea.onBrushEnd();

							if (interval != null) {
								onBrush(getTime(interval.start), getTime(interval.end));
								timeRangeHistory?.setValue((current: TimeRangeHistory) =>
									current.add(new SpecificRange(interval.start, interval.end)),
								);
							}
						});
					}
				}}
				ref={containerRef}
				responsive
				width="100%"
			>
				<Grid />

				<XAxis
					leftPadding={
						type === "line" ? (yAxis.show ? 20 + (yAxis.label ? Y_AXIS_LABEL_WIDTH_PX : 0) : undefined) : "no-gap"
					}
					timeRange={specificTimeRange}
				/>

				<Tooltip
					containerRef={containerRef}
					points={points}
					thresholds={thresholds}
					unit={unit}
					viewBoxElement={tooltipViewBoxElement}
				/>

				{labels.map((label, index) =>
					type === "line" ? (
						<Line
							connectNulls={connectNulls}
							index={index}
							key={index}
							legendState={state.legendContent}
							name={label}
						/>
					) : (
						<Bar index={index} key={index} legendState={state.legendContent} name={label} />
					),
				)}

				{showLegend && (
					<Legend
						content={
							<LegendContent
								className={cx(
									"shrink-0",
									/**
									 * Limit the legend width to 1/3 OR 1/2 of the chart area width,
									 * so it does not take too much space on the right side of the chart.
									 */
									!isBottomLegend && legendMode === "list" && "max-w-[min(208px,33%)]",
									!isBottomLegend && legendMode === "table" && "max-w-[min(620px,75%)]",
								)}
								columns={legendColumns}
								maxLines={isBottomLegend ? legendMaxLines : undefined}
								mode={legendMode}
								onMouseEnter={() => timestampCursor.setValue(undefined)}
								onShowAllTimeSeries={maxVisibleTimeSeries?.onShowAll}
								points={points}
								state={state.legendContent}
								timeSeries={visibleTimeSeries}
								totalTimeSeriesCount={timeSeries.length}
								unit={unit}
							/>
						}
						portal={legendPortalElement}
						wrapperStyle={{ display: "contents" }}
					/>
				)}

				<BrushArea offsetTop={Y_AXIS_PADDING_TOP_PX} state={state.brushArea} />

				{thresholds.map(({ value, stroke, name }) => (
					<ThresholdReferenceLine color={stroke} key={name} y={value} />
				))}

				<YAxis
					{...yAxis}
					decimalPlaces={decimalPlaces}
					labelWidth={Y_AXIS_LABEL_WIDTH_PX}
					paddingTop={Y_AXIS_PADDING_TOP_PX}
					unit={unit}
				/>

				<GlobalCursor
					// Hide the GlobalCursor for this chart if it is hovered, as it overlaps with the Tooltip
					className="group-hover/time-series-chart:hidden"
					points={type === "bar" ? points : undefined}
				/>

				<TimestampMarkers />

				{markerTimestamp != null && <TimestampCursor timestamp={markerTimestamp} />}
			</ChartElement>

			{/* https://main--63da8268a0da9970db6992aa.chromatic.com/iframe.html?globals=&args=&id=api-component-legend--legend-portal */}
			<div className="contents" ref={setLegendPortalElement} />
		</div>
	);
};
